import type { RealtimeGame, RealtimeState } from "../realtime";
import { createRng } from "../rng";

/**
 * Colour sort: pour the tubes until each holds one colour.
 *
 * The fourth game on the realtime contract, and the first that is a puzzle
 * rather than a reflex. It fits anyway, and the fit is what makes the contract
 * worth having: a run is still one child playing alone, still a list of presses
 * replayed by the server, still a race against a friend on identical boards.
 * What changes is only what the clock is for — here it is not the difficulty,
 * it is the length of the race. Two minutes, and whoever finishes more levels.
 *
 * Levels come from the seed and their own number, so both children get exactly
 * the same ones in the same order, and nothing has to be stored or sent.
 */

/** How many units of colour a tube holds. */
export const SORTER_CAPACITY = 4;
/** Empty tubes to work in. Two is the fewest that makes most levels tractable. */
export const SORTER_SPARE_TUBES = 2;
export const SORTER_FIRST_COLOURS = 3;
export const SORTER_MAX_COLOURS = 8;

export const SORTER_RATE = 8;
/** Two minutes, which is the race rather than the difficulty. */
export const SORTER_MAX_STEPS = 960;
/** What finishing a level is worth. Whole numbers, like every other score. */
export const SORTER_LEVEL_SCORE = 100;

export interface SorterState extends RealtimeState {
	kind: "sorter";
	/** Which level, counting from nought. */
	level: number;
	/** Each tube bottom-first. Colours are 1 and up; a tube may be empty. */
	tubes: number[][];
	/** The tube waiting to be poured, or null. Part of the state, not the view:
	 * a tap is the input, so what a tap means has to replay identically. */
	picked: number | null;
	/** Levels finished. The score is this and nothing else. */
	solved: number;
	/** Pours made on this level, so a child can see they are getting somewhere. */
	moves: number;
}

/** A tap on a tube, or a request to start this level again. */
export type SorterInput = { tap: number } | { restart: true };

/** How many colours a level has. Grows, then stops: eight is already a lot. */
export function sorterColours(level: number): number {
	return Math.min(SORTER_FIRST_COLOURS + Math.floor(level / 2), SORTER_MAX_COLOURS);
}

/** The colour on top of a tube, or 0 for an empty one. */
const topOf = (tube: readonly number[]): number => tube[tube.length - 1] ?? 0;

/** How many of the top colour sit together on top. */
function runOf(tube: readonly number[]): number {
	const colour = topOf(tube);
	if (colour === 0) return 0;
	let run = 0;
	for (let at = tube.length - 1; at >= 0 && tube[at] === colour; at--) run++;
	return run;
}

/** Whether a pour is allowed, and how much would move. */
export function sorterPourable(tubes: readonly (readonly number[])[], from: number, to: number) {
	if (from === to) return 0;
	const source = tubes[from];
	const target = tubes[to];
	if (!source || !target || source.length === 0) return 0;
	if (target.length >= SORTER_CAPACITY) return 0;
	if (target.length > 0 && topOf(target) !== topOf(source)) return 0;
	// Pouring a whole tube into an empty one achieves nothing and lets a child
	// shuffle for ever without noticing they are going nowhere.
	if (target.length === 0 && runOf(source) === source.length) return 0;
	return Math.min(runOf(source), SORTER_CAPACITY - target.length);
}

/** Every tube holds one colour, or nothing. */
export function sorterSolved(tubes: readonly (readonly number[])[]): boolean {
	return tubes.every((tube) => tube.length === 0 || runOf(tube) === tube.length);
}

/**
 * A level, built backwards from the finished puzzle.
 *
 * Scrambling at random and hoping is the obvious way and the wrong one: a
 * random arrangement of colours need not be sortable at all, and a child stuck
 * on a level that cannot be done has been handed a bug, not a challenge. Every
 * move here is the exact inverse of a legal pour, so the finished state is
 * always still reachable — by construction, with no solver to run and nothing
 * to verify at the other end.
 */
export function sorterLevel(seed: number, level: number): number[][] {
	const colours = sorterColours(level);

	// The level number is mixed into the seed so each level draws from its own
	// stream. Without it, consecutive levels were prefix-related scrambles of
	// one stream — each level the previous one plus a handful of unpours.
	// A few goes at it, so a scramble that happens to undo itself is dealt with
	// by trying again rather than by calling this function from inside itself.
	let tubes: number[][] = [];
	for (let go = 0; go < 32; go++) {
		tubes = scramble((seed + level * 2_654_435_761 + go * 7919) >>> 0, colours, 24 + level * 8);
		if (!sorterSolved(tubes)) return tubes;
	}
	// Thirty-two solved scrambles in a row does not happen; if it somehow did,
	// the last attempt is at least a fresh draw rather than the first one — the
	// one board already known to be dead.
	return tubes;
}

/**
 * Undoes `unpours` legal pours, starting from the finished puzzle.
 *
 * The condition on the target is the whole thing, and getting it wrong the
 * first time made every level come out already solved. Undoing a pour takes
 * some of a tube's top colour and puts it back where it came from — and where
 * it came from is a tube whose top was that same colour *before* the pour, so
 * what is under it can be anything at all. Requiring the target to match the
 * colour, as a forward pour does, means colours only ever split apart and never
 * mix: every tube stays uniform, and a uniform board is a finished one.
 *
 * The target must therefore *not* already show that colour. And the amount
 * taken matters just as much: taking a *whole* run off a tube that has more
 * beneath exposes a different colour, and the pour-back — which must land on
 * its own colour or an empty tube — has nowhere legal to go, so the chain of
 * inverses breaks and the level may not be solvable at all. An unpour is only
 * exact when some of the run stays behind to receive the pour-back, or when it
 * empties the tube entirely and leaves an empty to pour into.
 */
function scramble(seed: number, colours: number, unpours: number): number[][] {
	const rng = createRng(seed >>> 0);

	const tubes: number[][] = [];
	for (let colour = 1; colour <= colours; colour++) {
		tubes.push(new Array(SORTER_CAPACITY).fill(colour));
	}
	for (let spare = 0; spare < SORTER_SPARE_TUBES; spare++) tubes.push([]);

	for (let n = 0; n < unpours; n++) {
		const from = rng.int(tubes.length);
		const source = tubes[from];
		if (source.length === 0) continue;

		const colour = topOf(source);
		const run = runOf(source);
		// The whole run may only go if it is the whole tube; otherwise at least
		// one unit stays behind, so the pour-back always has its colour to land
		// on. This is what makes every unpour an exact inverse of a legal pour.
		const most = run === source.length ? run : run - 1;
		if (most === 0) continue;
		const take = 1 + rng.int(most);

		const targets: number[] = [];
		for (let to = 0; to < tubes.length; to++) {
			if (to === from) continue;
			const target = tubes[to];
			if (target.length + take > SORTER_CAPACITY) continue;
			if (topOf(target) === colour) continue;
			targets.push(to);
		}
		if (targets.length === 0) continue;

		const to = targets[rng.int(targets.length)];
		for (let unit = 0; unit < take; unit++) {
			tubes[to].push(source.pop()!);
		}
	}

	return tubes;
}

export const sorter: RealtimeGame<SorterState, SorterInput> = {
	id: "sorter",
	rate: SORTER_RATE,
	maxSteps: SORTER_MAX_STEPS,

	create(seed) {
		return {
			kind: "sorter",
			step: 0,
			seed,
			over: false,
			score: 0,
			level: 0,
			tubes: sorterLevel(seed, 0),
			picked: null,
			solved: 0,
			moves: 0,
		};
	},

	parseInput(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { tap, restart } = raw as { tap?: unknown; restart?: unknown };
		if (restart === true) return { restart: true };
		if (typeof tap !== "number" || !Number.isInteger(tap)) return null;
		// Bounded here rather than trusted: this is an index off the wire, and
		// the most tubes any level has is every colour plus the spares.
		if (tap < 0 || tap >= SORTER_MAX_COLOURS + SORTER_SPARE_TUBES) return null;
		return { tap };
	},

	step(state, input) {
		if (state.over) return state;
		const next: SorterState = { ...state, step: state.step + 1 };

		if (input && "restart" in input) {
			// Back to the start of this level, which is always the same board.
			return { ...next, tubes: sorterLevel(state.seed, state.level), picked: null, moves: 0 };
		}

		if (!input) return next;
		const tap = input.tap;
		if (tap >= state.tubes.length) return next;

		if (state.picked === null) {
			// An empty tube cannot be poured from, so tapping one selects nothing
			// rather than arming a move that will silently do nothing.
			return { ...next, picked: state.tubes[tap].length > 0 ? tap : null };
		}
		if (state.picked === tap) return { ...next, picked: null };

		const amount = sorterPourable(state.tubes, state.picked, tap);
		if (amount === 0) {
			// Not a legal pour. Treat it as picking the new tube instead, which is
			// what a child means by tapping it and saves them a tap.
			return { ...next, picked: state.tubes[tap].length > 0 ? tap : null };
		}

		const tubes = state.tubes.map((tube) => [...tube]);
		const colour = topOf(tubes[state.picked]);
		for (let unit = 0; unit < amount; unit++) {
			tubes[state.picked].pop();
			tubes[tap].push(colour);
		}

		if (!sorterSolved(tubes)) {
			return { ...next, tubes, picked: null, moves: state.moves + 1 };
		}

		const level = state.level + 1;
		return {
			...next,
			tubes: sorterLevel(state.seed, level),
			picked: null,
			level,
			solved: state.solved + 1,
			moves: 0,
			score: (state.solved + 1) * SORTER_LEVEL_SCORE,
		};
	},
};

/**
 * Robo sorting: finish a tube if you can, tidy a colour onto its own kind if
 * you can, and otherwise put something somewhere it fits.
 *
 * Not a solver — it does not look ahead at all, so it fills the spare tubes and
 * gets stuck like a person does. That is the point: a computer that solved
 * every level instantly would set a score no child could approach, and this one
 * sets a pile of finished levels that a thoughtful nine-year-old can beat.
 */
export function sorterTidy(state: SorterState): SorterInput | null {
	// Two taps make a pour, so the policy alternates: pick, then place.
	if (state.picked !== null) {
		const best = bestTarget(state.tubes, state.picked);
		// Nothing to do with it: put it down rather than pouring it somewhere silly.
		return { tap: best ?? state.picked };
	}

	for (let from = 0; from < state.tubes.length; from++) {
		if (state.tubes[from].length === 0) continue;
		if (bestTarget(state.tubes, from) !== null) return { tap: from };
	}
	// Boxed in. Start the level again rather than tapping about for two minutes.
	return { restart: true };
}

/** Where a tube's top colour would best go, or null if nowhere. */
function bestTarget(tubes: readonly number[][], from: number): number | null {
	let onto: number | null = null;
	let empty: number | null = null;

	for (let to = 0; to < tubes.length; to++) {
		if (sorterPourable(tubes, from, to) === 0) continue;
		if (tubes[to].length === 0) {
			empty ??= to;
			continue;
		}
		// Finishing a tube beats any other pour, so take it at once.
		const after = tubes[to].length + sorterPourable(tubes, from, to);
		if (after === SORTER_CAPACITY) return to;
		onto ??= to;
	}
	return onto ?? empty;
}
